import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Lock as LockIcon,
  Delete as DeleteIcon,
  CompareArrows as CompareIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon,
  Info as InfoIcon,
  People as PeopleIcon,
  QuestionAnswer as ResponsesIcon,
} from '@mui/icons-material';
import { format, parseISO, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import type { RFQ, RFQStatus, RFQResponse } from '../types';
import { rfqService } from '../services/rfqService';
import { dataService } from '../services/dataService';
import { colors } from '../theme';

// ============================================================================
// Configuración
// ============================================================================

const rfqStatusColors: Record<RFQStatus, string> = {
  draft: '#9e9e9e',
  sent: '#2196f3',
  responses: '#4caf50',
  closed: '#616161',
};

const rfqStatusLabels: Record<RFQStatus, string> = {
  draft: 'Borrador',
  sent: 'Enviada',
  responses: 'Respondida',
  closed: 'Cerrada',
};

// ============================================================================
// Tab Panel Component
// ============================================================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

// ============================================================================
// Main Component
// ============================================================================

const RFQDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [rfq, setRfq] = useState<RFQ | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Add supplier (draft mode)
  const [availableSuppliers, setAvailableSuppliers] = useState<string[]>([]);

  // =========================================================================
  // Carga de datos
  // =========================================================================

  useEffect(() => {
    if (!id) {
      navigate('/rfq');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        await dataService.loadPurchaseOrders('/00_Informacion utilizada/PurchaseOrders.csv');
        setAvailableSuppliers(dataService.getSuppliers());
      } catch (error) {
        console.error('[RFQDetail] Error cargando datos:', error);
      }

      const found = rfqService.getById(id);
      if (!found) {
        navigate('/rfq');
        return;
      }
      setRfq(found);
      setLoading(false);
    };

    loadData();

    // Escuchar cambios
    const handleUpdate = () => {
      if (id) {
        const updated = rfqService.getById(id);
        if (updated) setRfq(updated);
      }
    };
    window.addEventListener('rfqsUpdated', handleUpdate);
    return () => window.removeEventListener('rfqsUpdated', handleUpdate);
  }, [id, navigate]);

  // =========================================================================
  // Handlers
  // =========================================================================

  const handleSend = () => {
    if (!rfq) return;
    rfqService.send(rfq.id);
    setRfq(rfqService.getById(rfq.id));
    setSendDialogOpen(false);
    setSnackbar({
      open: true,
      message: `✉️ Compulsa enviada a ${rfq.suppliers.length} proveedor${rfq.suppliers.length > 1 ? 'es' : ''}`,
      severity: 'success',
    });
  };

  const handleClose = () => {
    if (!rfq) return;
    rfqService.close(rfq.id);
    setRfq(rfqService.getById(rfq.id));
    setCloseDialogOpen(false);
    setSnackbar({
      open: true,
      message: `🔒 Compulsa ${rfq.number} cerrada`,
      severity: 'info',
    });
  };

  const handleDelete = () => {
    if (!rfq) return;
    rfqService.delete(rfq.id);
    setDeleteDialogOpen(false);
    navigate('/rfq');
  };

  const handleAddSupplier = (supplierName: string) => {
    if (!rfq) return;
    const existingIds = rfq.suppliers.map(s => s.supplierName);
    if (existingIds.includes(supplierName)) return;

    const updatedSuppliers = [
      ...rfq.suppliers,
      {
        supplierId: `supplier-${supplierName.toLowerCase().replace(/\s+/g, '-')}`,
        supplierName,
        contactEmail: `compras@${supplierName.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 15)}.com`,
        hasResponded: false,
      },
    ];

    rfqService.update(rfq.id, { suppliers: updatedSuppliers });
    setRfq(rfqService.getById(rfq.id));
  };

  // =========================================================================
  // Helpers
  // =========================================================================

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
      return '—';
    }
  };

  const formatDateShort = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: es });
    } catch {
      return '—';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // =========================================================================
  // Response analysis helpers
  // =========================================================================

  const getBestPrice = (responses: RFQResponse[]): string | null => {
    if (responses.length < 2) return null;
    const sorted = [...responses].sort((a, b) => a.totalAmountUSD - b.totalAmountUSD);
    return sorted[0].supplierId;
  };

  const getBestDelivery = (responses: RFQResponse[]): string | null => {
    if (responses.length < 2) return null;
    const sorted = [...responses].sort((a, b) => a.deliveryDays - b.deliveryDays);
    return sorted[0].supplierId;
  };

  // =========================================================================
  // Loading/not found
  // =========================================================================

  if (loading || !rfq) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  const deadlinePast = isPast(parseISO(rfq.deadline));
  const bestPriceId = getBestPrice(rfq.responses);
  const bestDeliveryId = getBestDelivery(rfq.responses);

  return (
    <Box>
      {/* Breadcrumb + Header */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/rfq')}
        >
          Compulsas & RFQ
        </Link>
        <Typography color="text.primary">{rfq.number}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/rfq')}>
            <ArrowBackIcon />
          </IconButton>
          <div>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {rfq.number}
              </Typography>
              <Chip
                label={rfqStatusLabels[rfq.status]}
                sx={{
                  bgcolor: rfqStatusColors[rfq.status],
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary">
              {rfq.title}
            </Typography>
          </div>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {rfq.status === 'draft' && (
            <>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => setSendDialogOpen(true)}
                sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
              >
                Enviar
              </Button>
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Eliminar
              </Button>
            </>
          )}
          {rfq.status === 'sent' && (
            <Button
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={() => setCloseDialogOpen(true)}
            >
              Cerrar Compulsa
            </Button>
          )}
          {rfq.status === 'responses' && (
            <>
              <Button
                variant="contained"
                startIcon={<CompareIcon />}
                onClick={() => navigate(`/rfq/${rfq.id}/compare`)}
                sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
              >
                Ver Comparación
              </Button>
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setCloseDialogOpen(true)}
              >
                Cerrar Compulsa
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<InfoIcon />} iconPosition="start" label="Información" />
          <Tab icon={<PeopleIcon />} iconPosition="start" label={`Proveedores (${rfq.suppliers.length})`} />
          <Tab icon={<ResponsesIcon />} iconPosition="start" label={`Respuestas (${rfq.responses.length})`} />
        </Tabs>

        {/* Tab 1: Información */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3, pt: 0 }}>
            <Grid container spacing={3}>
              {/* Datos Generales */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Datos Generales
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Número:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2" fontWeight={600}>{rfq.number}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Título:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{rfq.title}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Categoría:</Typography></Grid>
                      <Grid item xs={8}><Chip label={rfq.mainCategory} size="small" variant="outlined" /></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Creado:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{formatDate(rfq.createdAt)}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Creado por:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{rfq.createdBy}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" color="text.secondary">Fecha límite:</Typography></Grid>
                      <Grid item xs={8}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: deadlinePast && rfq.status !== 'closed' ? colors.error.main : 'text.primary',
                          }}
                        >
                          {formatDateShort(rfq.deadline)}
                          {deadlinePast && rfq.status !== 'closed' && (
                            <Chip
                              label="VENCIDA"
                              size="small"
                              sx={{ ml: 1, bgcolor: colors.error.main, color: 'white', fontSize: '0.65rem', height: 20 }}
                            />
                          )}
                        </Typography>
                      </Grid>

                      {rfq.sentAt && (
                        <>
                          <Grid item xs={4}><Typography variant="body2" color="text.secondary">Enviada:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2">{formatDate(rfq.sentAt)}</Typography></Grid>
                        </>
                      )}

                      {rfq.closedAt && (
                        <>
                          <Grid item xs={4}><Typography variant="body2" color="text.secondary">Cerrada:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2">{formatDate(rfq.closedAt)}</Typography></Grid>
                        </>
                      )}

                      {rfq.notes && (
                        <>
                          <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                          <Grid item xs={4}><Typography variant="body2" color="text.secondary">Notas:</Typography></Grid>
                          <Grid item xs={8}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{rfq.notes}</Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Items Solicitados */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Items Solicitados ({rfq.items.length})
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Categoría</TableCell>
                            <TableCell>Descripción</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell>Unidad</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rfq.items.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{item.category}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{item.description || '—'}</Typography>
                                {item.specs && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    📋 {item.specs}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={600}>{item.quantity}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{item.unit}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 2: Proveedores */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3, pt: 0 }}>
            {/* Add supplier if draft */}
            {rfq.status === 'draft' && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Agregar proveedor
                </Typography>
                <Autocomplete
                  options={availableSuppliers.filter(s => !rfq.suppliers.some(existing => existing.supplierName === s))}
                  onChange={(_, newValue) => {
                    if (newValue) handleAddSupplier(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Buscar proveedor para agregar..."
                    />
                  )}
                  value={null}
                  blurOnSelect
                />
              </Paper>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Proveedor</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Estado de Envío</TableCell>
                    <TableCell align="center">Estado de Respuesta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rfq.suppliers.map((supplier) => (
                    <TableRow key={supplier.supplierId}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {supplier.supplierName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {supplier.contactEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {supplier.sentAt ? (
                          <Tooltip title={`Enviado ${formatDate(supplier.sentAt)}`}>
                            <Chip
                              icon={<SendIcon sx={{ fontSize: '14px !important' }} />}
                              label="Enviado"
                              size="small"
                              sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            label="No enviado"
                            size="small"
                            sx={{ bgcolor: '#f5f5f5', color: '#9e9e9e' }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {supplier.hasResponded ? (
                          <Tooltip title={`Respondió ${formatDate(supplier.respondedAt)}`}>
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                              label="Respondió"
                              size="small"
                              sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            icon={<PendingIcon sx={{ fontSize: '14px !important' }} />}
                            label="Pendiente"
                            size="small"
                            sx={{ bgcolor: '#fff8e1', color: '#f57c00' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tab 3: Respuestas */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3, pt: 0 }}>
            {rfq.responses.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{ p: 6, textAlign: 'center', bgcolor: '#fafafa' }}
              >
                <ResponsesIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  Aún no se recibieron respuestas
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                  {rfq.status === 'draft'
                    ? 'Envíe la compulsa primero para recibir cotizaciones'
                    : 'Las respuestas de proveedores aparecerán aquí'}
                </Typography>
              </Paper>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {rfq.responses.map((response) => (
                    <Grid item xs={12} sm={6} md={4} key={response.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderLeft: 4,
                          borderLeftColor:
                            bestPriceId === response.supplierId
                              ? colors.success.main
                              : bestDeliveryId === response.supplierId
                                ? colors.info.main
                                : 'divider',
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {response.supplierName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {bestPriceId === response.supplierId && (
                                <Chip
                                  icon={<MoneyIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Mejor Precio"
                                  size="small"
                                  sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.7rem' }}
                                />
                              )}
                              {bestDeliveryId === response.supplierId && (
                                <Chip
                                  icon={<ShippingIcon sx={{ fontSize: '14px !important' }} />}
                                  label="Mejor Plazo"
                                  size="small"
                                  sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>

                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Monto Total</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {formatCurrency(response.totalAmountUSD)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Plazo</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {response.deliveryDays} días
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Pago</Typography>
                              <Typography variant="body2">{response.paymentTerms}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Garantía</Typography>
                              <Typography variant="body2">{response.warranty || '—'}</Typography>
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 1.5 }} />

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Válida hasta: {formatDateShort(response.validUntil)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Recibida: {formatDateShort(response.submittedAt)}
                            </Typography>
                          </Box>

                          {response.notes && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                              {response.notes}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Botón Ver Comparación */}
                <Box sx={{ textAlign: 'center' }}>
                  <Tooltip
                    title={rfq.responses.length < 2 ? 'Se necesitan al menos 2 respuestas para comparar' : ''}
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<CompareIcon />}
                        onClick={() => navigate(`/rfq/${rfq.id}/compare`)}
                        disabled={rfq.responses.length < 2}
                        sx={{
                          bgcolor: colors.brand.red,
                          '&:hover': { bgcolor: colors.primary.dark },
                          '&.Mui-disabled': { bgcolor: '#e0e0e0' },
                        }}
                      >
                        Ver Comparación Detallada
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Send Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)}>
        <DialogTitle>Enviar Compulsa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Enviar la compulsa <strong>{rfq.number}</strong> a <strong>{rfq.suppliers.length}</strong> proveedor{rfq.suppliers.length > 1 ? 'es' : ''}?
            Se simulará el envío de email a cada proveedor.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSend}
            variant="contained"
            startIcon={<SendIcon />}
            sx={{ bgcolor: colors.brand.red, '&:hover': { bgcolor: colors.primary.dark } }}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Dialog */}
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)}>
        <DialogTitle>Cerrar Compulsa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Cerrar la compulsa <strong>{rfq.number}</strong>? No se aceptarán más respuestas de proveedores.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleClose}
            variant="contained"
            startIcon={<LockIcon />}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminar Compulsa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Eliminar la compulsa <strong>{rfq.number}</strong> — "{rfq.title}"?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RFQDetail;
